import { makeAutoObservable } from 'mobx';
import { ICreateDebtFeatureViewModel } from '@shared/types/mobx/features/OsiDebts';
import { inject, injectable } from 'inversify';
import { getOsiAbonents } from '@shared/api/osi/abonents/get';
import { notifications } from '@shared/utils/notifications';
import { Abonent } from '@shared/types/osi/abonents';
import logger from 'js-logger';
import api from '@app/api';
import { ServiceGroupResponse } from '@shared/types/osi/services';
import { AreaTypeCodes } from '@shared/types/dictionaries';
import { getPastDebts } from '@shared/api/pastDebts/get';
import { updatePastDebts } from '@shared/api/pastDebts/post';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';

const initialDebt: { saldo: number | null; list: any[] } = {
  saldo: null,
  list: []
};

@injectable()
export class CreateDebtFeatureViewModel implements ICreateDebtFeatureViewModel {
  isDialogOpen = false;

  isConfirmDialogOpen = false;

  isLoading = false;

  groups: ServiceGroupResponse[] = [];

  debt = initialDebt;

  confirmData: any = null;

  private _abonents: Abonent[] = [];

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);
    this.loadAbonents();
    this.loadGroups();
  }

  private _form: any = null;

  set form(form: any) {
    this._form = form;
  }

  private _refreshCb: any = null;

  set refreshCb(value: any) {
    this._refreshCb = value;
  }

  getAbonents = (serviceGroupId: number | null) => {
    if (!serviceGroupId) return [];
    // Фаза 1: определяем группу ADDITIONAL по code вместо хардкода ID=7
    const group = this.groups.find((g) => g.id === serviceGroupId);
    if (group?.code === 'ADDITIONAL') {
      // Миграция: external удалён из БД; арендатор определяется по areaTypeCode
      return this._abonents.filter((abonent) => abonent.areaTypeCode === AreaTypeCodes.ARENDATOR);
    }
    return this._abonents.filter((abonent) => abonent.areaTypeCode !== AreaTypeCodes.ARENDATOR);
  };

  loadDebts = async (abonentId: number, serviceGroupId: number) => {
    try {
      const debts = await getPastDebts(abonentId, serviceGroupId);

      const { saldo, pastDebts } = debts;

      this.debt.saldo = saldo ?? null;
      this.debt.list =
        pastDebts
          ?.map((x, i) => ({ id: i, ...x }))
          .sort((a, b) => new Date(a.period).getDate() - new Date(b.period).getDate()) ?? [];
    } catch (e) {
      logger.error(e);
      notifications.error();
    }
  };

  startCreatingDebt = () => {
    this.resetState();
    this.isDialogOpen = true;
  };

  cancelCreatingDebt = () => {
    this.isDialogOpen = false;
    this.resetState();
    this._refreshCb?.();
  };

  resetState = () => {
    this._form?.reset();
    this.debt = initialDebt;
    this.confirmData = null;
  };

  openConfirmDialog = () => {
    this.isConfirmDialogOpen = true;
  };

  onConfirm = async () => {
    try {
      const { period, amount } = this.confirmData;
      const abonentId = this._form?.getValues('abonent')?.id;
      const serviceGroupId = this._form?.getValues('serviceGroup')?.id;
      if (!abonentId || !serviceGroupId) throw new Error('abonentId or serviceGroupId is undefined');
      await updatePastDebts(abonentId, serviceGroupId, [{ period, amount }]);
    } catch (err) {
      notifications.error(err?.toString());
    } finally {
      this.onCancel();
    }
  };

  onCancel = () => {
    this.isConfirmDialogOpen = false;
  };

  onCellEditCommit = async (params: any) => {
    const { id, value } = params;

    const row = params.row || this.debt.list[id];
    if (id === 0 && value !== this.debt.saldo) {
      this.confirmData = { ...row, amount: value };
      this.openConfirmDialog();
      return;
    }
    try {
      if (!row || !row.period) throw new Error('Сетевой сбой');
      const { period } = row;
      const abonentId = this._form?.getValues('abonent')?.id;
      const serviceGroupId = this._form?.getValues('serviceGroup')?.id;
      if (!abonentId || !serviceGroupId) throw new Error('abonentId or serviceGroupId is undefined');

      await updatePastDebts(abonentId, serviceGroupId, [{ period, amount: value }]);
    } catch (err) {
      notifications.error(err?.toString());
    } finally {
      this.onCancel();
    }
  };

  private loadAbonents = async () => {
    try {
      if (!this.osiModule.osiId) throw new Error('osiId is undefined');
      this.isLoading = true;
      this._abonents = await getOsiAbonents(this.osiModule.osiId);
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };

  private loadGroups = async () => {
    try {
      if (!this.osiModule.osiId) throw new Error('osiId is undefined');
      this.isLoading = true;
      this.groups = (await api.OsiServicesV2(this.osiModule.osiId)) as unknown as ServiceGroupResponse[];
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };
}
